import { Config, fetchTDS } from '@/utils/fetch';
import { WebhookConfig } from '@/utils/webhook';
import { APITimeoutException } from '@/utils/exceptions/APITimeoutException';
import { TrailwaysInvalidTravelDateException } from '@/utils/exceptions/TrailwaysInvalidTravelDateException';

export type Schedules = {
  scheduleProducts: ScheduleProduct[];
};

export type ScheduleProduct = {
  amenities?: any[];
  fares?: Fares;
  railgunFares?: RailgunFares;
  scheduleRun: ScheduleRun;
  segments: Segment[];
  routeType?: string;
};

export type ScheduleRun = {
  alreadyDeparted: boolean;
  isUnavailable?: boolean;
  arriveTime: string;
  carrier: Carrier;
  departTime: string;
  destination: Stop;
  expired: boolean;
  leavesState?: boolean;
  mileage: number;
  occurrence?: number;
  origin: Stop;
  pastSalesCutoffTime: boolean;
  remainingCapacity: number;
  scheduleInstanceId?: number;
  scheduleNumber: string;
  scheduleOriginDepartDate?: string;
  scheduleUuid: string;
  soldOut: boolean;
  travelDate: string;
  travelDuration: number;
  trueNumber: string;
};

type Carrier = {
  carrierId: number;
  code: string;
  name?: string;
  phone?: string;
};

export type Stop = {
  address?: string;
  city: City;
  iataCode?: string;
  latitude?: number;
  longitude?: number;
  state: State;
  stationCode?: string;
  stationName: string;
  stopUuid: string;
  timeZone: string;
  zip?: string;
  displayBusinessName?: string;
  stopId?: string;
};

type City = {
  cityId: number;
  name: string;
};

type State = {
  name: string;
  abbreviation: string;
  country: string;
};

export type Segment = {
  arriveStop: Stop;
  arriveTime: string;
  departStop: Stop;
  departTime: string;
  layoverDuration: number;
  midnightLayover?: boolean;
  schedule: Schedule;
  transfer: boolean;
  travelDuration?: number;
};

type Schedule = {
  carrier: Carrier;
  scheduleNumber: string;
  scheduleUuid: string;
  trueNumber: string;
};

type RailgunFares = {
  Adult: Adult[];
  Senior?: Adult[];
  Child?: Adult[];
  flex?: Fare;
  saver?: Fare;
};

type Adult = {
  fares: Fare[];
};

export type Fare = {
  amount: number;
  availability?: string;
  couponApplied?: boolean;
  couponId?: any;
  fareId?: number | string;
  isMarkup?: boolean;
  markup: boolean;
  mileageFareDetails?: MileageFareDetails;
  name?: string;
  originalAmount?: any;
  passengerType?: string;
  specialFareDetails?: SpecialFareDetails;
  type: string;
};

type SpecialFareDetails = {
  amountBeforeMarkup: number;
  cancelable: boolean;
  fareId: number;
  latestRescheduleDate?: string;
  markupAmount: number;
  markupApplied: boolean;
  markupInstanceId: string;
  markupName?: string;
  maxMinutesAfterDepartureCanReschedule?: string;
  maxMinutesBeforeDepartureApplyFee?: string;
  minMinutesSameDirectionReserveAllowed?: string;
  numAdults?: number;
  numChildren?: number;
  numCollege?: number;
  numMilitary?: number;
  numSeniors?: number;
  promoCodeAllowed: boolean;
  remainingUses?: number;
  rescheduleable?: boolean;
  rescheduleAllPassengers: boolean;
  rescheduleCarryover: boolean;
  rescheduleFeeApplies: boolean;
};

type MileageFareDetails = {
  baseFare: number;
  endorsement?: any;
  fareAdjustments?: FareAdjustment[];
  fareCalcId?: string;
  fareCalcName?: string;
  fareCalcRuleId?: string;
  fareId: string;
  markup: string;
  markupApplied: boolean;
  markupId: string;
  mileageFareRateId?: string;
  mileageFareTable?: string;
  mileageFareTableId?: string;
  passengerType: string;
  surchargeId?: any;
  tariff?: string;
  tariffDescription?: string;
};

type FareAdjustment = {
  adjustedFare: number;
  adjustmentName?: string;
  adjustmentType: string;
  baseFare: number;
  delta: number;
};

type Fares = {
  MARKUP: {
    Adult: Array<{
      amount: number;
      availability: string;
      cancelable: boolean;
      groupRate: boolean;
      name?: string;
      passengerType?: string;
      rescheduleable: boolean;
      rescheduleAllPassengers: boolean;
      roundTrip: boolean;
      type?: string;
    }>;
  };
  ASSIGNED: {
    Adult: Array<{
      amount: number;
      availability: string;
      cancelable: boolean;
      groupRate: boolean;
      name?: string;
      passengerType?: string;
      rescheduleable: boolean;
      rescheduleAllPassengers: boolean;
      roundTrip: boolean;
      type?: string;
    }>;
  };
};

export type Customer = {
  address1?: string;
  address2?: string;
  carrier: {
    carrierId: number;
    code: string;
    name?: string;
    phone?: string;
  };
  city?: string;
  country?: {
    countryId: number;
    name?: string;
    countryAbbrev?: string;
    country3LetterAbbrev?: string;
  };
  customerId: number;
  defaultCard?: number;
  email: string;
  firstName: string;
  lastName: string;
  mobile?: string;
  phone?: string;
  state?: string;
  zip?: string;
  accountStatus?: string;
};

type LoyaltyChange = {
  pointChange: number;
  earnType: string;
};

export type LoyaltyTransaction = {
  customerId: number;
  transactionDate: string;
  orderId: number;
  numTransactions: number;
  loyaltyChange: LoyaltyChange;
};

export type Loyalty = {
  totalLoyalty: number;
  totalLoyaltyUsed: number;
  totalLoyaltyPoints: number;
  totalLoyaltyDollars: number;
  loyaltyTransactions: LoyaltyTransaction[];
  redeemable?: boolean;
};

export type TDSScheduleCityToCityRequestBody = {
  departDate: string;
  destination?: {
    stopUuid: string;
  };
  destinationCity?: string;
  origin?: {
    stopUuid: string;
  };
  originCity?: string;
  passengerCounts: {
    Adult: number;
    Military: number;
    Senior: number;
    Child: number;
    Student: number;
  };
  isReturn: boolean;
  purchaseType: string;
  returnDate?: string;
};

export type TDSScheduleRequestBody = {
  departDate: string;
  destination: {
    stopUuid: string;
  };
  origin: {
    stopUuid: string;
  };
  passengerCounts: {
    Adult: number;
    Military: number;
    Senior: number;
    Child: number;
    Student: number;
  };
  isReturn: boolean;
  purchaseType: string;
};

export type ScheduleTrip = {
  scheduleRun: ScheduleRun;
  segments: Segment[];
};

export interface Passenger {
  passengerId: number;
  firstName: string;
  lastName: string;
  passengerType: string;
  wheelchair: boolean;
  outboundStatus: string;
  returnStatus: string;
}

export type TDSCommuterSearchRequestBody = {
  originStopStationUUID: string;
  destStopStationUUID: string;
  travelStartDate: string;
};

interface Bill {
  outboundFareTotal: number;
  returningFareTotal: number;
  subTotal: number;
  baggageFees: number;
  requiredFeeTotal: number;
  addonsTotal: number;
  discountTotal: number;
  tax: number;
  taxDetail: any[];
  credits: number;
  usedReissueCredits: number;
  usedAccruedCredits: number;
  remainingCredits: number;
  loyalty: number;
  totalBeforeDeductions: number;
  total: number;
}

export type Reservation = {
  bill: Bill;
  confirmationCode: string;
  customer: Customer;
  isCommuter: boolean;
  orderDate: string;
  orderId: number;
  outbound: ScheduleTrip;
  passengers: Passenger[];
  refundURL?: string;
  returning: ScheduleTrip;
  ticketHash: string;
  ticketUrl: string;
};

export type RewardsHistory = {
  id: number;
  pointChange: number;
  outbound: ScheduleTrip;
  returning: ScheduleTrip;
  roundTrip: boolean;
};

type PassDetails = {
  confirmationCode: string;
  creditUsed: number | null;
  departDate: string;
  discount: number;
  effectiveDate: string;
  expirationDate: string;
  fare: number;
  fareId: number;
  fareName: string;
  itemId: number;
  orderId: number;
  orderStarted: string;
  passId: number;
  remaining: number;
  rescheduleable: boolean;
  reserved: number;
  restrictionText: string;
  tax: number;
  total: number;
  totalUses: number;
  used: number;
};
export type ReissueRequest = {
  departDate: string;
  destination: {
    id: string;
    name: string;
    stationCode: string;
    stopUuid: string;
    value: string;
  };
  destinationCity?: string;
  origin: {
    id: string;
    name: string;
    stationCode: string;
    stopUuid: string;
    value: string;
  };
  originCity?: string;
  purchaseType: string;
  reissueRequest: {
    orderConfirmationCode: string;
    passengers: {
      directions: string[];
      passenger: {
        passengerId: number;
        type?: string;
      };
    }[];
  };
};

export type ReservationDetails = {
  confirmationCode: string;
  confirmationNumber: number;
  currencyConversionRate: number;
  currencyPaid: string;
  destination: Stop;
  directions: string[];
  discount: number;
  fareType?: string;
  isCommuter: boolean;
  orderDate: string;
  origin: Stop;
  outbound: ScheduleRun;
  passengers: {
    destination: Stop;
    firstName: string;
    lastName: string;
    origin: Stop;
    outboundFare: Fare;
    passengerId: number;
    returnFare: Fare;
    type: string;
  }[];
  payments: {
    amount: number;
    approved: boolean;
    authorizationCode: string;
    avsCode: null | string;
    cardPresent: boolean;
    cardType: string;
    checkNumber: null | string;
    cvvResponse: null | string;
    firstSix: string;
    lastFour: string;
    offlinePaymentType: null | string;
    paymentDate: string;
    transactionId: string;
    transactionType: string;
  }[];
  receipt: Receipt;
  returning: ScheduleRun;
  ticketURL: string;
};

type Receipt = {
  address1: string;
  city: string;
  country: string;
  credit: number;
  discountTotal: number;
  email: string;
  fares: number;
  feeDetail: Record<string, number>;
  feeDetails: {
    carryOverAdditional: null | string;
    description: string;
    perItem: number;
    price: number;
    quantity: number;
    required: boolean;
  }[];
  fees: number;
  firstName: string;
  lastName: string;
  loyalty: number;
  mobile: string;
  paymentType: string;
  phone: string;
  processedBy: {
    barCode: string;
    carrierId: number;
    code: string;
    name: string;
    phone: string;
    timeZoneId: number;
  };
  remainingCredits: number;
  state: string;
  tax: number;
  total: number;
  totalAmountPaid: number;
  zip: string;
};

export type CommuterStop = Stop & {
  address2: string;
  displayCity: string;
  displayState: string;
  doorToDoor: boolean;
  extraInfoRequired: boolean;
  extraInfoType: string | null;
  operators: any[];
  routeType: string | null;
  stopId: number;
  stopType: string | null;
};

type Manifest = {
  arriveStop: CommuterStop;
  carrier: string | null;
  departStop: CommuterStop;
  itemId: number;
  layoutId: string | null;
  liftDate: string | null;
  lifted: boolean;
  occurrence: number;
  outboundTrip: boolean;
  partialFare: number;
  partialMileage: number;
  scheduleDepartDate: string | null;
  scheduleId: string | null;
  scheduleManifestId: number;
  scheduleNumber: string | null;
  scheduleUuid: string | null;
  ticketNumber: string;
  ticketOrder: number;
};

export type CommuterPass = {
  passDetails: PassDetails;
  passPackageDepartCity: City;
  passPackageArriveCity: City;
  departStop: CommuterStop;
  arriveStop: CommuterStop;
  manifests: Manifest[];
  schedulesFor?: string[];
};

export type TDSScheduleCommuterRequestBody = {
  originId: number;
  destinationId: number;
  startDate: string;
  passId: number;
};
// Check if the travel date is in the past.  If so, that's an invalid travel date
// Since we only have MM/DD/YYYY to work with, and no knowledge of timezone at this point,
// we can't be certain about this.  So this is not to be used as a definitive check.
// We should also add these checks upstream, but this is a last line of defense.
const isValidTravelDate = (dateString: string) => {
  return true;
  /* Disabled for now, as had issues
  const now = new Date();
  now.setTime(now.getTime() - 10800000); // Subtract 3 hours to allow for the full range of continental US timezones
  now.setHours(0, 0, 0, 0);
  const tempTravelDate = new Date(dateString);
  const timezoneOffset = tempTravelDate.getTimezoneOffset() * 60000;
  const travelDate = new Date(tempTravelDate.getTime() + timezoneOffset);
  travelDate.setHours(0, 0, 0, 0); // Shouldn't be needed, but just in case datestring is more than just MM/DD/YYYY
  return travelDate >= now;
  */
};

export const getCustomerDetail = async () => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 600 },
    path: 'customer/detail'
  };
  try {
    return await fetchTDS(config);
  } catch (error) {}
};

export const getCustomerForgotPw = async (data: { [key: string]: string }) => {
  const config: Config = {
    data,
    method: 'POST',
    path: 'customer/forgot-pw'
  };
  return await fetchTDS(config);
};

export const editCustomer = async (data: { [key: string]: string }) => {
  const config: Config = {
    data,
    method: 'PUT',
    path: 'customer/update'
  };
  return await fetchTDS(config);
};

export const getCustomerResetPw = async (data: { [key: string]: string }) => {
  const config: Config = {
    data,
    method: 'POST',
    path: 'customer/reset-pw'
  };
  return await fetchTDS(config);
};

export const getLoyalty = async () => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 600 },
    path: 'loyalty/total'
  };
  try {
    return await fetchTDS(config);
  } catch (error) {}
};

export const logoutCustomer = async () => {
  const config: Config = {
    method: 'POST',
    path: 'user/logout'
  };
  try {
    return await fetchTDS(config);
  } catch (error) {}
};

export const getReservation = async (orderNumber: string, lastName: string | null) => {
  const config: Config = {
    method: 'GET',
    path: 'reservation',
    params: {
      confirmationNumber: orderNumber,
      lastName: lastName ? lastName : ''
    }
  };
  try {
    return await fetchTDS(config);
  } catch (error) {}
};

export const getReservationByConfirmationCode = async (confirmationCode: string) => {
  const config: Config = {
    method: 'GET',
    path: `reservation/${confirmationCode}`
  };
  return await fetchTDS(config);
};

export const getSchedules = async (
  data: TDSScheduleRequestBody,
  seconds_to_cache: number,
  webhookConfig?: WebhookConfig
) => {
  if (typeof window == 'undefined') {
    // Prevent searching for schedules in the past
    if (!isValidTravelDate(data.departDate)) {
      throw new TrailwaysInvalidTravelDateException(data.departDate);
    }
  }

  const config: Config = {
    data,
    method: 'POST',
    next: { revalidate: seconds_to_cache },
    path: 'v2/schedule'
  };
  if (process.env.NEXT_PUBLIC_MOCK_MODE) {
    config.headers = {
      'x-mock-response-name': `${data.origin.stopUuid}|${data.destination.stopUuid}|${data.isReturn.toString()}`
    };
  }
  let scheduleWebhookConfig: WebhookConfig = { endpoint: 'sched' };
  if (webhookConfig) {
    scheduleWebhookConfig.userAgent = webhookConfig.userAgent;
  }
  try {
    return await fetchTDS(config, scheduleWebhookConfig);
  } catch (error) {
    if (error instanceof APITimeoutException) {
      throw error;
    }
  }
};

export const getAndCacheReissueSchedules = async (
  data: ReissueRequest,
  seconds_to_cache: number,
  cookie: string | undefined
) => {
  const config: Config = {
    data,
    method: 'POST',
    next: { revalidate: seconds_to_cache },
    path: 'v2/schedule'
  };
  if (cookie) {
    config.headers = { cookie };
  }
  const scheduleWebhookConfig: WebhookConfig = { endpoint: 'sched' };
  try {
    return await fetchTDS(config, scheduleWebhookConfig);
  } catch (error) {
    if (error instanceof APITimeoutException) {
      throw error;
    }
  }
};

export const getSignedIn = async () => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 60 },
    path: 'customer/detail'
  };
  return await fetchTDS(config);
};

export const searchSchedules = async (data: TDSScheduleCityToCityRequestBody) => {
  // Additional /schedules call to retrieve the fares for each passenger type when there are non-adult passengers
  const nonAdultPassengers = data.passengerCounts.Senior + data.passengerCounts.Child;
  const resultsAllPassengerTypePromise =
    nonAdultPassengers !== 0
      ? fetchTDS({
          data,
          method: 'POST',
          next: { revalidate: 60 },
          path: 'schedules'
        })
      : Promise.resolve(null);

  data = {
    ...data,
    passengerCounts: {
      ...data.passengerCounts,
      Adult: data.passengerCounts.Adult + data.passengerCounts.Senior + data.passengerCounts.Child,
      Senior: 0,
      Child: 0
    }
  };
  const resultsPromise = fetchTDS({
    data,
    method: 'POST',
    next: { revalidate: 60 },
    path: 'schedules'
  });
  const [resultsAllPassengerType, results] = await Promise.all([resultsAllPassengerTypePromise, resultsPromise]);
  return {
    results,
    resultsAllPassengerType
  };
};

export const searchSchedulesForReschedule = async (data: ReissueRequest) => {
  const config: Config = {
    data,
    method: 'POST',
    next: { revalidate: 60 },
    path: 'schedules'
  };
  return await fetchTDS(config);
};

export const userLogin = async (data: { [key: string]: string }) => {
  const credentials = btoa(`${data.username}:${data.password}`);
  const config: Config = {
    headers: {
      Authorization: `Basic ${credentials}`
    },
    method: 'POST',
    path: 'user/login'
  };
  return await fetchTDS(config);
};

export const createUser = async (data: { [key: string]: any }) => {
  const config: Config = {
    data,
    method: 'POST',
    path: 'user/'
  };
  return await fetchTDS(config);
};

export const getAvailability = async (data: { [key: string]: any }) => {
  // Prevent searching for schedules in the past
  if (typeof window == 'undefined') {
    if (!isValidTravelDate(data.outbound.departDate)) {
      throw new TrailwaysInvalidTravelDateException(data.outbound.departDate);
    }
  }
  const config: Config = {
    data,
    method: 'POST',
    path: 'v2/availability'
  };
  if (process.env.NEXT_PUBLIC_MOCK_MODE) {
    config.headers = {
      'x-mock-response-name': `Price Increase`
    };
  }
  return await fetchTDS(config);
};

export const deleteCart = async () => {
  const config: Config = {
    method: 'DELETE',
    path: 'cart'
  };
  return await fetchTDS(config);
};

export const getUserPaymentInfo = async () => {
  const config: Config = {
    method: 'GET',
    path: 'customer/payment/stored'
  };
  return await fetchTDS(config);
};

export const deleteCard = async (storedPaymentId: number) => {
  const config: Config = {
    method: 'DELETE',
    path: `customer/payment/stored/${storedPaymentId}`
  };
  return await fetchTDS(config);
};

export const bookTicket = async (data: { [key: string]: any }) => {
  const config: Config = {
    data,
    method: 'POST',
    path: 'book'
  };
  return await fetchTDS(config);
};

export const findReservation = async (data: { [key: string]: any }, confirmationCode: string) => {
  const config: Config = {
    data,
    method: 'POST',
    path: `reservation/${confirmationCode}`
  };
  return await fetchTDS(config);
};

export const checkBooking = async (key: string) => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 0 },
    path: `check-booking/${key}`
  };
  return await fetchTDS(config);
};

export const deleteBookingInfoKey = async (key: string) => {
  const config: Config = {
    method: 'GET',
    next: { revalidate: 0 },
    path: `delete-booking-info/${key}`
  };
  return await fetchTDS(config);
};

export const pastReservations = async (page: number, size: number) => {
  const config: Config = {
    method: 'GET',
    path: `customer/v2/trips/previous?page=${page}&size=${size}`
  };
  return await fetchTDS(config);
};

export const upcomingReservations = async (page: number, size: number) => {
  const config: Config = {
    method: 'GET',
    path: `customer/v2/trips/upcoming?page=${page}&size=${size}`
  };
  return await fetchTDS(config);
};

export const assignReservation = async (confirmationCode: string, customerId: number) => {
  const config: Config = {
    method: 'PUT',
    path: `reservation/assign-reservation/${confirmationCode}/${customerId}`
  };
  return await fetchTDS(config);
};

export const commuterSearch = async (data: TDSCommuterSearchRequestBody) => {
  const config: Config = {
    data,
    method: 'POST',
    path: 'v1/commuter/search'
  };
  return await fetchTDS(config);
};

export const commuterPassBookTicket = async (data: { [key: string]: any }) => {
  const config: Config = {
    data,
    method: 'POST',
    path: 'v2/book'
  };
  return await fetchTDS(config);
};

export const getCommuterPasses = async () => {
  const config: Config = {
    method: 'GET',
    path: 'v1/commuter/passes'
  };
  return await fetchTDS(config);
};

export const searchCommuterSchedules = async (data: TDSScheduleCommuterRequestBody) => {
  const config: Config = {
    data,
    method: 'POST',
    path: 'v2/schedule/commuter'
  };
  return await fetchTDS(config);
};

export const cancelRide = async (confirmationCode: string, manifestId: number | null) => {
  const config: Config = {
    data: { confirmationCode, manifestId },
    method: 'POST',
    path: 'v1/cancel/commuter'
  };
  return await fetchTDS(config);
};

export const getCreditsDetails = async () => {
  const config: Config = {
    method: 'GET',
    path: 'credits/details'
  };
  return await fetchTDS(config);
};
